import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {ProductSelection_DisplayType} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_selection.types';
import {GetProductBulkResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {useTrackFeature, useTrackOriginClick} from '@aboutyou/tracking';
import {
  ClientGate,
  ColorLabel,
  ContentPopperProps,
  ImageType,
  ProductColorOptions,
  ProductColorOptionsProps,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {useActivatedCouponState} from '../../../features/Coupons/useActivatedCouponState';
import {COUPON_PROMOTION_ID_QUERY_KEY} from '../../../features/Coupons/useCouponPromotionIdFromQueryParams';
import {useQuickViewVariant} from '../QuickView/useQuickViewVariant';
import {ProductSelectionGrid} from './ProductSelectionGrid';
import {ProductSelectionHorizontal} from './ProductSelectionHorizontal';
import {ProductSelectionList} from './ProductSelectionList';
import {SoldOutSection} from './SoldOut';

export type SoldOutComponentProps = {
  colorOptions: ProductColorOptionsProps['options'] | null;
  onSoldOutButtonClick?: VoidFunction;
  ColorOptionsComponent: React.ReactNode;
  condensed?: boolean;
  product?: Product;
};

export type ProductSelectionViewProps = {
  className?: string;
  disablePreviewImage?: boolean;
  disableRedirect?: boolean;
  onProductSelectionClick?: (productId: number) => void;
  ColorSelectionLayoutComponent: ContentPopperProps['LayoutComponent'];
  showProductSelectionAsGrid?: boolean;
  uniqueKey?: string;
  hideHeader?: boolean;
  onSoldOutButtonClick?: VoidFunction;
  SoldOutComponent?: React.FC<SoldOutComponentProps>;
  setPreviewImage?: (preview?: ImageType) => void;
  setImageGridInactive?: (isActive: boolean) => void;
  isInMiniADPDialog?: boolean;
};

export const ProductSelectionView: React.FC<
  GetProductBulkResponse['productSelectionSection'] & ProductSelectionViewProps
> = ({
  product,
  type,
  disablePreviewImage,
  onProductSelectionClick,
  ColorSelectionLayoutComponent,
  className,
  uniqueKey,
  disableRedirect,
  hideHeader,
  showProductSelectionAsGrid,
  onSoldOutButtonClick,
  SoldOutComponent,
  setPreviewImage,
  setImageGridInactive,
  isInMiniADPDialog,
}) => {
  const trackOriginClick = useTrackOriginClick();
  const trackFeature = useTrackFeature();

  const {t} = useTranslation();
  const quickViewVariant = useQuickViewVariant();
  const isQuickView = quickViewVariant !== 'adp';
  const {couponPromotionId} = useActivatedCouponState(product.id);

  const productSelection =
    type?.$case === 'productSelection' ? type?.productSelection : null;

  const colorItems = productSelection?.items;
  const displayType = productSelection?.displayType;

  const getColorOptionPath = (path: string, isSoldOut: boolean) => {
    // Preserve the current quick view variant so switching color stays in the
    // same variant (v1 or v2) instead of falling back to the old quick view.
    const params = new URLSearchParams();
    if (quickViewVariant !== 'adp' && !isSoldOut) {
      params.set(
        'quick',
        quickViewVariant === 'quickViewV2' ? 'view1' : 'view',
      );
    }

    if (couponPromotionId) {
      params.set(COUPON_PROMOTION_ID_QUERY_KEY, couponPromotionId);
    }

    return params.size > 0 ? `${path}?${params.toString()}` : path;
  };

  const colorOptions =
    colorItems?.map(({thumbnailImage, path, id, colorLabel, isSoldOut}, i) => {
      return {
        image: {
          url: thumbnailImage.src,
          description: thumbnailImage.description,
          padded: thumbnailImage.isEnclosed,
        },
        isActive: id === product?.id,
        isSoldOut,
        path: getColorOptionPath(path, isSoldOut),
        name: colorLabel,
        onClick: () => {
          trackOriginClick('style_picker')();
          onProductSelectionClick?.(id);

          if (isSoldOut) {
            trackFeature({eventType: 'click', label: 'sold_out_color'});
          }

          // we need to enable the product image grid again when selecting another product color
          setPreviewImage?.();
          setImageGridInactive?.(false);
        },
        'data-testid': `productColorListOption${i}`,
      };
    }) ?? null;

  const SoldOut = SoldOutComponent ?? SoldOutSection;

  if (product?.isSoldOut) {
    // rendering the `SoldOutComponent` fetcher on the server creates a network waterfall and results is slow loading of the soldout ADP
    // rendering it client only as a mitigation
    return (
      <ClientGate>
        <SoldOut
          productImage={product?.primaryBustImage}
          colorOptions={colorOptions}
          className={className}
          condensed={isQuickView}
          onSoldOutButtonClick={onSoldOutButtonClick}
          product={product}
          isInMiniADPDialog={isInMiniADPDialog}
          ColorOptionsComponent={
            <SoldOutColorOptions
              colorOptions={colorOptions}
              showProductSelectionAsGrid={showProductSelectionAsGrid}
              setPreviewImage={setPreviewImage}
              setImageGridInactive={setImageGridInactive}
              disableRedirect={disableRedirect}
            />
          }
        />
      </ClientGate>
    );
  }

  if (type?.$case === 'noSiblings') {
    const activeName = type?.noSiblings.colorLabel;
    return activeName ? (
      <ColorLabel
        className={className}
        data-testid={`productColorInfo`}
        label={t('Colour')}
        name={activeName}
      />
    ) : null;
  }

  if (!colorOptions) {
    return null;
  }

  if (showProductSelectionAsGrid) {
    return (
      <StyledProductSelectionGrid
        className={className}
        colorOptions={colorOptions}
        setPreviewImage={setPreviewImage}
        setImageGridInactive={setImageGridInactive}
        disablePreviewImage={disablePreviewImage}
        uniqueKey={uniqueKey}
        key={product.id}
      />
    );
  }

  if (displayType === ProductSelection_DisplayType.COLOR_LIST) {
    return (
      <ProductSelectionList
        ColorSelectionLayoutComponent={ColorSelectionLayoutComponent}
        colorOptions={colorOptions}
        className={className}
        onSoldOutButtonClick={onSoldOutButtonClick}
      />
    );
  }

  return (
    <ProductSelectionHorizontal
      colorOptions={colorOptions}
      className={className}
      disablePreviewImage={disablePreviewImage}
      disableRedirect={disableRedirect}
      setPreviewImage={setPreviewImage}
      setImageGridInactive={setImageGridInactive}
      shouldUseSquareProductImages
      hideHeader={hideHeader}
      onSoldOutButtonClick={onSoldOutButtonClick}
    />
  );
};

const SoldOutColorOptions: React.FC<{
  colorOptions: ProductColorOptionsProps['options'] | null;
  showProductSelectionAsGrid?: boolean;
  setPreviewImage: ProductSelectionViewProps['setPreviewImage'];
  setImageGridInactive?: ProductSelectionViewProps['setImageGridInactive'];
  disableRedirect?: boolean;
}> = ({
  colorOptions,
  showProductSelectionAsGrid,
  setPreviewImage,
  setImageGridInactive,
  disableRedirect,
}) => {
  if (!colorOptions) {
    return null;
  }

  if (showProductSelectionAsGrid) {
    return (
      <StyledProductSelectionGrid
        disablePreviewImage
        colorOptions={colorOptions}
        setPreviewImage={setPreviewImage}
        setImageGridInactive={setImageGridInactive}
      />
    );
  }

  return (
    <StyledProductColorOptions
      disablePreviewImage
      disableRedirect={disableRedirect}
      options={colorOptions}
      setPreviewImage={setPreviewImage}
      setImageGridInactive={setImageGridInactive}
    />
  );
};

export const StyledProductSelectionGrid = styled(ProductSelectionGrid)`
  text-align: left;
`;

const StyledProductColorOptions = styled(ProductColorOptions)`
  margin-bottom: 1rem;
`;
